import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Paper,
  IconButton,
  Skeleton,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axiosInstance from '../../lib/axios';
import type { ResellerProfile } from '../../types/b2b';
import { palette } from '../../theme/colors';

// Activity statistics interface from API
interface ActivityStats {
  lastActivityAt: string | null;
  lastActivityType : string | null;
  totalOrders: number;
  totalOrderValue: number;
  totalQuotesRequested: number;
  accountAge: number;
}

const fetchReseller = async (id: string): Promise<ResellerProfile> => {
  const response = await axiosInstance.get(`/reseller-profiles/${id}`);
  return response.data;
};

const fetchActivityStats = async (id: string): Promise<ActivityStats> => {
  const response = await axiosInstance.get(`/reseller-profiles/${id}/activity`);
  return response.data;
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Jamais';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTierColor = (tierName: string) => {
  switch (tierName.toLowerCase()) {
    case 'platinum':
      return '#E5E4E2';
    case 'gold':
      return '#FFD700';
    case 'silver':
      return '#C0C0C0';
    case 'bronze':
      return '#CD7F32';
    default:
      return palette.info;
  }
};

export const ResellerShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: reseller, isLoading: isLoadingReseller, error: resellerError } = useQuery({
    queryKey: ['reseller-profile', id],
    queryFn: () => fetchReseller(id!),
    enabled: !!id,
  });

  const { data: activityStats, isLoading: isLoadingActivity } = useQuery({
    queryKey: ['reseller-activity', id],
    queryFn: () => fetchActivityStats(id!),
    enabled: !!id,
  });

  if (isLoadingReseller) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/resellers')}>
            <ArrowBackIcon />
          </IconButton>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (resellerError || !reseller) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/resellers')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Revendeur non trouvé</Typography>
        </Box>
        <Card>
          <CardContent>
            <Typography color="error">
              Impossible de charger les détails du revendeur. Le revendeur n'existe peut-être pas ou vous n'avez pas la permission de le consulter.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/app/resellers')}
              sx={{ mt: 2 }}
            >
              Retour aux revendeurs
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/app/resellers')}>
          <ArrowBackIcon />
        </IconButton>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              bgcolor: palette.primary,
              width: 56,
              height: 56,
              fontSize: '1.5rem',
            }}
          >
            {reseller.companyName?.[0]?.toUpperCase() || <BusinessIcon />}
          </Avatar>
          <Box>
            <Typography variant="h4">{reseller.companyName}</Typography>
            <Typography variant="body2" color="text.secondary">
              Revendeur depuis le {new Date(reseller.createdAt).toLocaleDateString('fr-FR')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Chip
            icon={reseller.isApproved ? <CheckCircleIcon /> : <BlockIcon />}
            label={reseller.isApproved ? 'Actif' : 'Suspendu'}
            color={reseller.isApproved ? 'success' : 'error'}
            variant="filled"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Reseller Info */}
        <Grid item xs={12} md={6}>
          {/* Company Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations de l'entreprise
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BusinessIcon sx={{ color: palette.textMuted }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Nom de l'entreprise
                    </Typography>
                    <Typography>{reseller.companyName}</Typography>
                  </Box>
                </Box>
              </Box>
              {reseller.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notes
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: palette.surfaceElevated }}
                  >
                    <Typography variant="body2">{reseller.notes}</Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* User Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personne de contact
              </Typography>
              {reseller.user ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonIcon sx={{ color: palette.textMuted }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Nom
                      </Typography>
                      <Typography>
                        {reseller.user.firstName} {reseller.user.lastName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ color: palette.textMuted }} />
                    <Typography>{reseller.user.email}</Typography>
                  </Box>
                  {reseller.user.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PhoneIcon sx={{ color: palette.textMuted }} />
                      <Typography>{reseller.user.phone}</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">Aucune information de contact disponible</Typography>
              )}
            </CardContent>
          </Card>

          {/* Niveau de tarification Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Niveau de tarification
              </Typography>
              {reseller.defaultTier ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocalOfferIcon sx={{ color: getTierColor(reseller.defaultTier.tierName) }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Niveau actuel
                      </Typography>
                      <Typography sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {reseller.defaultTier.tierName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ pl: 4.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Remise
                    </Typography>
                    <Typography>
                      {reseller.defaultTier.discountType === 'percentage'
                        ? `${reseller.defaultTier.discountValue}%`
                        : formatCurrency(reseller.defaultTier.discountValue)}
                      {' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        ({reseller.defaultTier.discountType})
                      </Typography>
                    </Typography>
                  </Box>
                  {reseller.defaultTier.minimumQuantity > 1 && (
                    <Box sx={{ pl: 4.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Quantité minimum
                      </Typography>
                      <Typography>{reseller.defaultTier.minimumQuantity} unités</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    py: 3,
                    textAlign: 'center',
                    bgcolor: palette.surfaceElevated,
                    borderRadius: 2,
                  }}
                >
                  <LocalOfferIcon sx={{ fontSize: 40, color: palette.textMuted, mb: 1 }} />
                  <Typography color="text.secondary">
                    Aucun niveau de tarification assigné
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ce revendeur utilise les tarifs par défaut
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Activity & Statistics */}
        <Grid item xs={12} md={6}>
          {/* Approval Statut Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statut du compte
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {reseller.isApproved ? (
                    <CheckCircleIcon sx={{ color: palette.success }} />
                  ) : (
                    <BlockIcon sx={{ color: palette.error }} />
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Statut
                    </Typography>
                    <Typography sx={{ color: reseller.isApproved ? palette.success : palette.error }}>
                      {reseller.isApproved ? 'Actif - Accès au portail B2B autorisé' : 'Suspendu - Accès B2B révoqué'}
                    </Typography>
                  </Box>
                </Box>
                {reseller.approvedBy && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonIcon sx={{ color: palette.textMuted }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Approuvé par
                      </Typography>
                      <Typography>
                        {reseller.approvedBy.firstName} {reseller.approvedBy.lastName}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarTodayIcon sx={{ color: palette.textMuted }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Créé le
                    </Typography>
                    <Typography>
                      {formatDate(reseller.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Statistiques d'activité */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiques d'activité
              </Typography>
              {isLoadingActivity ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="rectangular" height={60} />
                  <Skeleton variant="rectangular" height={60} />
                  <Skeleton variant="rectangular" height={60} />
                </Box>
              ) : activityStats ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Dernière activité */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ScheduleIcon sx={{ color: palette.textMuted }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Dernière activité
                      </Typography>
                      <Typography>
                        {formatDate(activityStats.lastActivityAt)}
                      </Typography>
                      {activityStats.lastActivityType && (
                        <Typography variant="caption" color="text.secondary">
                          Type : {activityStats.lastActivityType.replace('_', ' ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Stats Grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    {/* Commandes totales */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: palette.surfaceElevated,
                      }}
                    >
                      <ShoppingCartIcon sx={{ fontSize: 32, color: palette.primary, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {activityStats.totalOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Commandes totales
                      </Typography>
                    </Paper>

                    {/* Valeur totale des commandes */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: palette.surfaceElevated,
                      }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: 32, color: palette.success, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(activityStats.totalOrderValue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Valeur totale des commandes
                      </Typography>
                    </Paper>

                    {/* Total Quotes */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: palette.surfaceElevated,
                      }}
                    >
                      <RequestQuoteIcon sx={{ fontSize: 32, color: palette.info, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {activityStats.totalQuotesRequested}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Devis demandés
                      </Typography>
                    </Paper>

                    {/* Account Age */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        bgcolor: palette.surfaceElevated,
                      }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 32, color: palette.warning, mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {activityStats.accountAge}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Âge du compte (jours)
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    bgcolor: palette.surfaceElevated,
                    borderRadius: 2,
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                  <Typography color="text.secondary">
                    Aucune donnée d'activité disponible
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
